import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';

import { AppButton, AppDropdown } from '@/components/atoms';
import useAppModal from '@/hooks/useAppModal';
import { patientsApi } from '@/services/patients';
import { useAuth } from '@shared/providers/AuthContext';
import { setLatestEditingPatientId } from '@shared/lib/latestEditingPatient';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { apiClient } from '@shared/lib/axios';
import { clinicApiPath } from '@shared/lib/apiPath';
import dayjs from 'dayjs';
import { isoMonthToJaWareki } from '@/utils/dateConverter';
import { getYearmonthSearch } from '@/services/main/visitClinic';

type PatientNavPage =
  | 'patient-edit'
  | 'consents'
  | 'treatment-reports'
  | 'patient-addresses'
  | 'long-term-frequents'
  | 'application-counts'
  | 'checkin-mails'
  | 'visits'
  | null;

type NavButtonKey =
  | 'insurance'
  | 'consent'
  | 'treatmentReport'
  | 'patientAddresses'
  | 'longTermFrequents'
  | 'applicationCounts'
  | 'checkinMails'
  | 'treatment'
  | 'visits'
  | 'templates';

const DISABLED_BUTTON_CLASS = '!bg-gray-200 !text-gray-500 cursor-not-allowed';

const BASE_ALLOWED_KEYS: NavButtonKey[] = [
  'insurance',
  'consent',
  'treatmentReport',
  'patientAddresses',
  'longTermFrequents',
  'applicationCounts',
  'checkinMails',
  'treatment',
  'visits',
  'templates',
];

const HISTORIES_ALLOWED_SET = new Set<NavButtonKey>(BASE_ALLOWED_KEYS);

/** YYYY-MM for check-existence / continuation edit (RA2N payload may omit visit_last_day). */
export function getTreatmentFlowContextFromPatient(
  patientData:
    | {
        id?: number | null;
        official?: boolean | null;
        visit_last_year_month?: string | null;
        visit_last_day?: string | null;
        patient?: { visit_last_day?: string | null } | null;
      }
    | null
    | undefined
): { official: boolean; visitLastYearMonth: string } | undefined {
  if (!patientData?.id) return undefined;
  const raw =
    patientData.patient?.visit_last_day ??
    patientData.visit_last_year_month ??
    patientData.visit_last_day;
  let ym = '';
  if (raw != null && String(raw).trim() !== '') {
    const s = String(raw).trim();
    if (/^\d{4}-\d{2}$/.test(s)) ym = s;
    else {
      const head = s.slice(0, 7);
      if (/^\d{4}-\d{2}$/.test(head)) ym = head;
    }
  }
  return { official: Boolean(patientData.official), visitLastYearMonth: ym };
}

interface PatientNavigationButtonsProps {
  id: string;
  tenant?: string;
  navigate: (path: string) => void;
  /** When on 患者編集 (patients/edit/:id), pass 'patient-edit' so 保険証 is disabled. When on 訪問住所一覧, pass 'patient-addresses' so 訪問住所 is disabled. */
  activeKey?: PatientNavPage;
  /** A2 施術: check insurance/consent then navigate to 施術入力 (`/visit_clinic/schedule`). `visitLastYearMonth` is used for existence check API only. Omit when patient context is unavailable. */
  treatmentFlowContext?: { official: boolean; visitLastYearMonth: string };
  /**
   * A2 legacy: `patients/edit.blade.php` No441/No672 lists all six 各種雛型 links.
   * Use `compact` only on screens that should keep the first three.
   * Default `full` matches patient edit, consents, treatment reports, etc.
   */
  pdfTemplateMenu?: 'full' | 'compact';
  disabled?: boolean;
}

/**
 * A2 legacy `patients/edit.blade.php` (and most patient-scoped screens): six dropdown rows after 訪問.
 * Maps to RA2N `GET .../pdf-templates/:type` (not legacy `/consents/:id/template` URLs).
 */
const TEMPLATE_ITEMS_FULL_A2: Array<{ label: string; type: string }> = [
  { label: '同意書(雛型)', type: 'consent' },
  { label: '施術報告書(雛型)', type: 'treatment-report' },
  { label: '16回(雛型)', type: 'long-frequent' },
  { label: '同意書依頼書(雛型)', type: 'consent-request' },
  { label: '頻回理由(雛型)', type: 'frequent-reason' },
  { label: '施術録(雛型)', type: 'treatment-record' },
];

const TEMPLATE_ITEMS_FULL_R2: Array<{ label: string; type: string }> = [
  { label: '同意書(雛型)', type: 'r-consent' },
  { label: '施術報告書(雛型)', type: 'treatment-report' },
  { label: '16回(雛型)', type: 'r-long-frequent' },
  { label: '同意書依頼書(雛型)', type: 'r-consent-request' },
  { label: '頻回理由(雛型)', type: 'r-frequent-reason' },
  { label: '施術録(雛型)', type: 'r-treatment-record' },
];

export type PdfTemplateItem = { label: string; type: string };

/** `full` = all six template items; `compact` = first three only. */
export function selectPdfTemplateItems(
  themeServiceType: string | undefined,
  scope: 'full' | 'compact'
): PdfTemplateItem[] {
  const full =
    themeServiceType === SERVICE_TYPE_R2 ? TEMPLATE_ITEMS_FULL_R2 : TEMPLATE_ITEMS_FULL_A2;
  return scope === 'compact' ? full.slice(0, 3) : full;
}

export const PatientPdfTemplatesDropdown: React.FC<{
  patientId: string;
  items: PdfTemplateItem[];
  disabled?: boolean;
}> = ({ patientId, items, disabled }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { clinic } = useAuth();

  const siteType = clinic?.site_type ?? '';
  const clinicNumber = clinic?.clinic_number ?? '';

  const handleTemplateClick = async (type: string) => {
    setOpen(false);
    if (!siteType || !clinicNumber) return;
    // Open the tab synchronously so the browser accepts it as user-initiated.
    // We can't defer until after the await: API can exceed the browser's
    // transient user-activation window (~5s in Chrome), after which popups
    // are silently blocked.
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(
        `<html><head><title>PDFを準備しています…</title></head>` +
          `<body style="margin:0;display:flex;align-items:center;justify-content:center;` +
          `height:100vh;font-family:sans-serif;color:#555;background:#f7f7f7">` +
          `PDFを準備しています…</body></html>`
      );
    }
    const path = clinicApiPath(
      siteType,
      clinicNumber,
      `/patients/${patientId}/pdf-templates/${type}`
    );
    setLoading(true);
    try {
      const res = await apiClient.get<Blob>(path, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      if (win && !win.closed) {
        win.location.replace(blobUrl);
      } else {
        // Popup was blocked or closed — fall back to anchor click.
        const a = document.createElement('a');
        a.href = blobUrl;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
    } catch {
      if (win && !win.closed) win.close();
    } finally {
      setLoading(false);
    }
  };

  const dropdownItems: MenuProps['items'] = items.map(it => ({
    key: it.type,
    label: it.label,
  }));

  return (
    <div className="relative shrink-0">
      <AppDropdown
        open={open}
        onOpenChange={nextOpen => setOpen(nextOpen)}
        trigger={['click']}
        placement="bottomRight"
        disabled={disabled}
        overlayClassName="min-w-56"
        menu={{
          items: dropdownItems,
          onClick: ({ key }) => void handleTemplateClick(String(key)),
        }}
      >
        <AppButton
          type="default"
          loading={loading}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          各種雛型 <i className="fa fa-caret-down" aria-hidden="true"></i>
        </AppButton>
      </AppDropdown>
    </div>
  );
};

const PatientNavigationButtons: React.FC<PatientNavigationButtonsProps> = ({
  id,
  tenant,
  navigate,
  activeKey = null,
  treatmentFlowContext,
  pdfTemplateMenu = 'full',
  // disabled = false,
}) => {
  const [treatmentLoading, setTreatmentLoading] = useState(false);
  const { clinic } = useAuth();
  const { serviceType: themeServiceType } = useTheme();
  const { info } = useAppModal();

  const siteType = clinic?.site_type ?? '';
  const clinicNumber = clinic?.clinic_number ?? '';
  const isCheckinEnabled = clinic?.is_enable_checkin === true;

  const templateItems = selectPdfTemplateItems(themeServiceType, pdfTemplateMenu);

  const isPatientEdit = activeKey === 'patient-edit';
  const isPatientAddresses = activeKey === 'patient-addresses';
  const isCheckinMails = activeKey === 'checkin-mails';
  const isConsents = activeKey === 'consents';
  const isApplicationCounts = activeKey === 'application-counts';
  const isTreatmentReports = activeKey === 'treatment-reports';
  const isLongTermFrequents = activeKey === 'long-term-frequents';

  const location = useLocation();
  const pathname = location.pathname ?? '';
  const isHistoriesPage = pathname.includes(`/patients/${id}/histories`);
  const isDmInfoEditPage = pathname.includes(`/patients/${id}/dminfo/edit`);
  const isHistoriesOrDmInfo = isHistoriesPage || isDmInfoEditPage;

  const patientEditAllowed = new Set<NavButtonKey>(BASE_ALLOWED_KEYS);

  const scopedAllowedSet = isPatientEdit
    ? patientEditAllowed
    : isHistoriesOrDmInfo || isConsents
      ? HISTORIES_ALLOWED_SET
      : null;

  const shouldShow = (key: NavButtonKey) => (scopedAllowedSet ? scopedAllowedSet.has(key) : true);

  const base = tenant ? `/${tenant}` : '';

  useEffect(() => {
    if (!id) return;
    setLatestEditingPatientId(tenant, id, clinicNumber);
  }, [tenant, id, clinicNumber]);

  const navigateAfterTreatmentCheck = (ym: string) => {
    const jaYm = isoMonthToJaWareki(ym);
    navigate(`${base}/visit_clinic/edit/${id}/${jaYm}`);
  };

  const resolveTreatmentYearmonth = async (patientId: number) => {
    try {
      const data = await getYearmonthSearch(siteType, clinicNumber, patientId, themeServiceType);
      if (/^\d{4}-\d{2}$/.test(data.yearmonth)) return data.yearmonth;
    } catch {
      // Fall back below; navigation should still work even if yearmonth lookup fails.
    }

    const contextYearmonth = treatmentFlowContext?.visitLastYearMonth?.trim();
    return contextYearmonth && /^\d{4}-\d{2}$/.test(contextYearmonth)
      ? contextYearmonth
      : dayjs().format('YYYY-MM');
  };

  const showTreatmentWarningsThenNavigate = async () => {
    if (!siteType || !clinicNumber) return;
    const patientId = parseInt(id, 10);
    if (Number.isNaN(patientId)) return;
    setTreatmentLoading(true);
    try {
      const ym = await resolveTreatmentYearmonth(patientId);
      const data = await patientsApi.checkExistence(siteType, clinicNumber, patientId, ym);

      const runNavigate = () => navigateAfterTreatmentCheck(ym);

      if (!data.response) {
        runNavigate();
        return;
      }

      const showConsentWarning = (then: () => void) => {
        if (data.no_consent) {
          info({
            title: '確認',
            content: '同意書の期限が切れています。同意書期限の更新を行ってください。',
            okText: 'OK',
            onOk: then,
          });
        } else {
          then();
        }
      };

      if (data.no_health_insurance_card) {
        info({
          title: '確認',
          content: '保険証が有効期限外です。保険証情報の日付をご確認ください。',
          okText: 'OK',
          onOk: () => showConsentWarning(runNavigate),
        });
      } else {
        showConsentWarning(runNavigate);
      }
    } finally {
      setTreatmentLoading(false);
    }
  };

  type ButtonDef = {
    key: Exclude<NavButtonKey, 'templates'>;
    label: React.ReactNode;
    disabled?: boolean;
    loading?: boolean;
    hide?: boolean;
    onClick: () => void;
  };

  const buttons: ButtonDef[] = [
    {
      key: 'insurance',
      label: '保険証',
      disabled: isPatientEdit,
      onClick: () => !isPatientEdit && navigate(`${base}/patients/edit/${id}`),
    },
    {
      key: 'consent',
      label: '同意書',
      disabled: isConsents,
      onClick: () => !isConsents && navigate(`${base}/consents/${id}/list`),
    },
    {
      key: 'treatmentReport',
      label: '施術報告書',
      disabled: isTreatmentReports,
      onClick: () => !isTreatmentReports && navigate(`${base}/treatment-reports/${id}/list`),
    },
    {
      key: 'patientAddresses',
      label: '訪問住所',
      disabled: isPatientAddresses,
      onClick: () => !isPatientAddresses && navigate(`${base}/patient_addresses/${id}/list`),
    },
    {
      key: 'longTermFrequents',
      label: '16回',
      disabled: isLongTermFrequents,
      onClick: () => !isLongTermFrequents && navigate(`${base}/long_term_frequents/${id}/list`),
    },
    {
      key: 'applicationCounts',
      label: '申請回数',
      disabled: isApplicationCounts,
      onClick: () =>
        !isApplicationCounts && navigate(`${base}/rezept_application_counts/${id}/list`),
    },
    {
      key: 'checkinMails',
      label: 'チェックイン通知',
      disabled: isCheckinMails,
      hide: !isCheckinEnabled,
      onClick: () => !isCheckinMails && navigate(`${base}/checkin_mails/${id}/list`),
    },
    {
      key: 'treatment',
      label: '施術',
      loading: treatmentLoading,
      hide: themeServiceType !== SERVICE_TYPE_A2,
      onClick: () => void showTreatmentWarningsThenNavigate(),
    },
    {
      key: 'visits',
      label: '訪問',
      onClick: () => navigate(`${base}/visits/schedule`),
    },
  ];

  return (
    <div className="flex max-w-full flex-wrap items-center gap-2 overflow-x-auto pb-1 xl:overflow-visible xl:pb-0">
      {buttons.map(
        b =>
          shouldShow(b.key) &&
          !b.hide && (
            <AppButton
              key={b.key}
              type="default"
              disabled={b.disabled}
              className={`shrink-0 ${b.disabled ? DISABLED_BUTTON_CLASS : ''}`}
              loading={b.loading}
              onClick={b.onClick}
            >
              {b.label}
            </AppButton>
          )
      )}

      {shouldShow('templates') && (
        <PatientPdfTemplatesDropdown patientId={id} items={templateItems} />
      )}
    </div>
  );
};

export default PatientNavigationButtons;
